// Compact dropdown currency selector with a light/dark variant.
//
// Usage:
//   <ToggleCurrency />            -> light variant (default), for cream
//                                    or white backgrounds (desktop Appbar,
//                                    cart page, GotoCart strip).
//   <ToggleCurrency variant="dark" /> -> dark variant, for the mobile
//                                        fixed bottom bar which sits on
//                                        Colors.shaft (#333).
//
// Same default export name and useShoppingCart wiring as before, so the
// three call sites can pass the prop or omit it.

import React from 'react'
import { Box, MenuItem, Select } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useShoppingCart } from '../../context/ShoppingCartContext'
import { Colors } from '../../styles/theme'
import USD from './usd.svg'
import CAD from './cad.svg'

// ─── Variant-aware color tokens ───────────────────────────────────────
//
// Keeping these in a small lookup keeps the styled-component logic
// readable. If a third context appears later (e.g. a footer placement),
// add a new variant entry here rather than sprinkling conditionals
// throughout the styles.
const VARIANTS = {
    light: {
        text: Colors.secondary,
        countryText: Colors.secondary,
        codeText: Colors.grayDark,
        border: Colors.boltGold,
        borderHover: Colors.greenLight,
        chevron: Colors.secondary,
        focusRing: 'rgba(60, 190, 114, 0.2)',
        // Menu (the popover) uses the light scheme regardless of trigger
        // variant, since the popover floats above the page on its own
        // surface and reading dark text on white is universally clear.
    },
    dark: {
        text: Colors.cream,
        countryText: Colors.cream,
        codeText: 'rgba(254, 244, 233, 0.75)', // muted cream
        border: Colors.boltGold,
        borderHover: Colors.cream,
        chevron: Colors.cream,
        focusRing: 'rgba(185, 134, 71, 0.4)', // gold-tinted on dark
    },
}

// ─── Styled bits ──────────────────────────────────────────────────────

const FlagIcon = styled('img')({
    width: 18,
    height: 'auto',
    display: 'block',
    borderRadius: 2,
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.12)',
    flexShrink: 0,
})

// Strips `variant` from the props that emotion would otherwise forward
// to the underlying DOM element (which would log a React warning about
// unknown attribute `variant`).
const StyledSelect = styled(Select, {
    shouldForwardProp: (prop) => prop !== 'variant',
})(({ variant = 'light' }) => {
    const v = VARIANTS[variant] || VARIANTS.light
    return {
        fontSize: '0.8rem',
        fontWeight: 600,
        color: v.text,
        backgroundColor: 'transparent',
        // 40px height = comfortable touch target (Apple HIG recommends 44px
        // minimum but 40 reads as less visually heavy in the dark mobile bar
        // and is still well above the "missable" threshold).
        height: 40,
        minWidth: 88,

        '& .MuiSelect-select': {
            padding: '8px 8px',
            paddingRight: '26px !important',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 'unset',
        },

        '& .MuiOutlinedInput-notchedOutline': {
            border: `1.5px solid ${v.border}`,
            borderRadius: 6,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            border: `1.5px solid ${v.borderHover}`,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1.5px solid ${v.borderHover}`,
            boxShadow: `0 0 0 2px ${v.focusRing}`,
        },

        '& .MuiSelect-icon': {
            color: v.chevron,
            right: 4,
            fontSize: '1.2rem',
        },

        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
    }
})

const StyledMenuItem = styled(MenuItem)({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: Colors.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',

    '&.Mui-selected': {
        backgroundColor: 'rgba(0, 82, 71, 0.1)',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: 'rgba(0, 82, 71, 0.15)',
        },
    },
})

// Renders the option content. Variant controls the colors used in the
// CLOSED-state display only — menu items always use light colors since
// the popover renders on a white surface.
function CurrencyOption({ flagSrc, country, code, variant = 'light' }) {
    const v = VARIANTS[variant] || VARIANTS.light
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagIcon src={flagSrc} alt="" />
            <Box
                component="span"
                sx={{ fontWeight: 600, color: v.countryText }}
            >
                {country}
            </Box>
            <Box component="span" sx={{ color: v.codeText, fontWeight: 500 }}>
                {code}
            </Box>
        </Box>
    )
}

// ─── Component ────────────────────────────────────────────────────────

const ToggleCurrency = ({ variant = 'light' }) => {
    const { isCAD, setIsCADCurrency } = useShoppingCart()
    const value = isCAD ? 'CAD' : 'USD'

    const handleChange = (event) => {
        setIsCADCurrency(event.target.value === 'CAD')
    }

    return (
        <StyledSelect
            variant={variant}
            value={value}
            onChange={handleChange}
            size="small"
            inputProps={{ 'aria-label': 'Currency selection' }}
            renderValue={(selected) =>
                selected === 'CAD' ? (
                    <CurrencyOption
                        flagSrc={CAD}
                        country="CA"
                        code="CAD"
                        variant={variant}
                    />
                ) : (
                    <CurrencyOption
                        flagSrc={USD}
                        country="US"
                        code="USD"
                        variant={variant}
                    />
                )
            }
        >
            {/* Menu items always use the light scheme — they render on
                the popover's white surface regardless of trigger variant. */}
            <StyledMenuItem value="USD">
                <CurrencyOption flagSrc={USD} country="US" code="USD" />
            </StyledMenuItem>
            <StyledMenuItem value="CAD">
                <CurrencyOption flagSrc={CAD} country="CA" code="CAD" />
            </StyledMenuItem>
        </StyledSelect>
    )
}

export default ToggleCurrency
